import React, {useEffect, useRef} from 'react';
import {Link, useLocation} from 'react-router-dom';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {useAuth} from '../../contexts/AuthContext';
import {navItemsFor} from './navItems';

interface BottomNavProps {
    /** Số việc đang chờ theo đường dẫn - cùng nguồn với chấm đỏ ở sidebar */
    badges?: Partial<Record<string, number>>;
}

/**
 * Thanh điều hướng đáy màn hình - chỉ trên điện thoại.
 *
 * Trước đây mọi lần chuyển trang trên điện thoại đều là: chạm nút mở menu →
 * drawer trượt ra → chọn mục → drawer đóng. Ba nhịp cho một việc làm mấy chục
 * lần mỗi tháng, trong khi ngón cái đang ở đáy màn còn menu thì ở góc trên bên
 * trái - góc xa nhất so với ngón cái.
 *
 * CHỈ LẤY BA MỤC ĐẦU của menu. Thanh đáy là lối tắt cho việc làm hằng ngày,
 * không phải bản sao của menu: nhồi cả Cài đặt và Phân quyền vào thì mỗi ô hẹp
 * lại, chữ phải cắt bớt, và hai mục dùng vài lần một năm chiếm chỗ ngang với
 * mục dùng hằng ngày. Drawer vẫn giữ đủ mọi mục.
 *
 * `navItemsFor` đã lọc theo vai trò rồi, nên người chỉ có quyền xem không thấy
 * mục họ không vào được. Cắt ba mục SAU khi lọc là cố ý - nếu cắt trước thì một
 * người thiếu quyền ở mục giữa sẽ nhận thanh đáy hai ô.
 */
export const BottomNav: React.FC<BottomNavProps> = ({badges = {}}) => {
    const location = useLocation();
    const {role} = useAuth();
    const navRef = useRef<HTMLElement>(null);

    const items = navItemsFor(role).slice(0, 3);

    /*
     * Báo chiều cao THẬT của thanh ra biến `--bottom-nav-height`, để AppLayout
     * chừa đúng ngần ấy chỗ ở cuối trang (class `.pb-bottom-nav`).
     *
     * Trước đây khung trang đoán bằng `pb-14` (3.5rem). Con số đó sai trên iPhone
     * vì thanh còn cộng thêm vùng an toàn của vạch Home (~34px), và sai lần nữa
     * khi người dùng bật cỡ chữ lớn làm nhãn cao lên. Cả hai lần đều đẩy dòng
     * cuối của trang xuống dưới thanh - nút ở đó bấm không tới.
     *
     * Đo bằng ResizeObserver chứ không tính tay: chiều cao đổi theo cỡ chữ hệ
     * thống, theo xoay ngang/dọc (inset đổi), và theo cả việc thanh có hiện hay
     * không. Đo thì không có trường hợp nào phải nhớ.
     */
    useEffect(() => {
        const node = navRef.current;
        if (!node) return;

        const root = document.documentElement;
        /* Chỉ ghi khi số thật sự đổi. Trên Safari iOS, `safe-area-inset-bottom`
           nhảy 0 ↔ ~34px mỗi lần thanh công cụ của trình duyệt thu vào/bung ra,
           nên callback này chạy liên tục trong lúc cuộn; ghi lại đúng giá trị cũ
           chỉ tổ bắt trình duyệt tính lại bố cục cho không. */
        const publish = () => {
            const next = `${node.offsetHeight}px`;
            if (root.style.getPropertyValue('--bottom-nav-height') !== next) {
                root.style.setProperty('--bottom-nav-height', next);
            }
        };
        publish();

        const observer = new ResizeObserver(publish);
        observer.observe(node);
        return () => {
            observer.disconnect();
            root.style.removeProperty('--bottom-nav-height');
        };
    }, [items.length]);

    if (items.length === 0) return null;

    const isActive = (path: string) =>
        location.pathname === path || location.pathname.startsWith(`${path}/`);

    return (
        <nav
            ref={navRef}
            className="lg:hidden fixed bottom-0 inset-x-0 z-30 glass-toolbar border-t
                border-white/50 dark:border-white/10 rounded-none safe-bottom"
            aria-label="Điều hướng nhanh"
        >
            <div className="flex items-stretch">
                {items.map(item => {
                    const active = isActive(item.path);
                    const badge = badges[item.path] ?? 0;

                    return (
                        <Link
                            key={item.path}
                            to={item.path}
                            aria-current={active ? 'page' : undefined}
                            className={`flex-1 min-h-[3.5rem] flex flex-col items-center justify-center gap-0.5
                                px-1 py-2 text-[11px] font-medium transition-colors
                                ${active
                                ? 'text-violet-700 dark:text-violet-300'
                                : 'text-slate-500 dark:text-slate-400'}`}
                        >
                            <span className="relative">
                                <FontAwesomeIcon icon={item.icon} className="w-5 h-5"/>
                                {badge > 0 && (
                                    <span
                                        className="absolute -top-1.5 -right-2 min-w-[1rem] h-4 px-1 rounded-full
                                            bg-rose-500 text-white text-[10px] font-bold
                                            flex items-center justify-center"
                                    >
                                        {badge > 9 ? '9+' : badge}
                                    </span>
                                )}
                            </span>
                            {/* Nhãn cắt ngắn: "Quản lý nhà trọ" không vừa một ô rộng
                                một phần ba màn hình điện thoại. Lấy từ cuối lên cho
                                ra "nhà trọ" / "điện nước" - phần mang nghĩa. */}
                            <span className="truncate max-w-full">
                                {item.label.split(' ').slice(-2).join(' ')}
                            </span>
                        </Link>
                    );
                })}
            </div>
        </nav>
    );
};
